


import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "./firebase";
import { isUsernameAllowed } from "./utils/usernameFilter";
import { unregisterCurrentNativePushToken } from "./services/nativePushNotifications";
import { isAdultAgeGroup } from "./utils/aiConflictConsent";

const LOGOUT_IN_PROGRESS_KEY = "vaultquest:logoutInProgress";

function setLogoutInProgress(value) {
  if (typeof window === "undefined") return;

  try {
    if (value) {
      window.sessionStorage?.setItem(LOGOUT_IN_PROGRESS_KEY, "1");
    } else {
      window.sessionStorage?.removeItem(LOGOUT_IN_PROGRESS_KEY);
    }
  } catch {
    // Session storage can be unavailable in private or restricted browser contexts.
  }
}

export const sendVerificationEmailForUser = async (user, language = "en") => {
  if (!user) return null;

  const normalizedLanguage = language === "de" ? "de" : "en";
  const functions = getFunctions();
  const sendVerificationEmailCustom = httpsCallable(
    functions,
    "sendVerificationEmailCustom"
  );

  return sendVerificationEmailCustom({
    language: normalizedLanguage,
  });
};

export const completeFederatedProfile = async (profile) => {
  const normalizedAge = String(profile?.age || "").trim() || null;
  const completeFederatedProfileSecure = httpsCallable(
    getFunctions(),
    "completeFederatedProfileSecure"
  );
  return completeFederatedProfileSecure({
    username: String(profile?.username || "").trim(),
    consoleType: String(profile?.consoleType || "").trim(),
    age: normalizedAge,
    language: profile?.language === "de" ? "de" : "en",
    timeZone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    confirmedMinimumAge: profile?.confirmedMinimumAge === true,
    acceptedLegal: profile?.acceptedLegal === true,
    aiConflictAnalysisConsent:
      profile?.aiConflictAnalysisConsent === true && isAdultAgeGroup(normalizedAge),
  });
};


// Registrierung
export const registerUser = async (
  email,
  password,
  username,
  age,
  consoleType,
  language = "en",
  aiConflictAnalysisConsent = false
) => {
  const usernameClean = String(username || "").trim();
  const usernameLower = usernameClean.toLowerCase();
  const emailLower = String(email || "").trim().toLowerCase();
  const normalizedAge = String(age || "").trim() || null;
    const normalizedLanguage = language === "de" ? "de" : "en";
    const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,15}$/;

const RESERVED_USERNAMES = [
  "admin",
  "support",
  "vaultquest",
  "official",
  "moderator",
  "system",
];

if (!USERNAME_REGEX.test(usernameClean)) {
  throw new Error("INVALID_USERNAME");
}

if (RESERVED_USERNAMES.includes(usernameLower)) {
  throw new Error("RESERVED_USERNAME");
}

if (!isUsernameAllowed(usernameClean)) {
  throw new Error("USERNAME_NOT_ALLOWED");
}

  const functions = getFunctions();
  const checkUsernameAvailabilitySecure = httpsCallable(
    functions,
    "checkUsernameAvailabilitySecure"
  );
  const availability = await checkUsernameAvailabilitySecure({
    username: usernameLower,
  });

  if (availability?.data?.available !== true) {
    throw new Error("USERNAME_ALREADY_TAKEN");
  }

let userCredential;

try {
  userCredential = await createUserWithEmailAndPassword(
    auth,
    emailLower,
    password
  );
} catch (error) {

  if (error.code === "auth/invalid-email") {
    throw new Error("INVALID_EMAIL");
  }

  throw error;
}

  const completePasswordProfileSecure = httpsCallable(
    functions,
    "completePasswordProfileSecure"
  );
  await completePasswordProfileSecure({
    username: usernameClean,
    consoleType: String(consoleType || "").trim(),
    age: normalizedAge,
    language: normalizedLanguage,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin",
    confirmedMinimumAge: true,
    acceptedLegal: true,
    aiConflictAnalysisConsent: aiConflictAnalysisConsent === true,
  });

  const syncMyPublicProfileSecure = httpsCallable(
    functions,
    "syncMyPublicProfileSecure"
  );
  await syncMyPublicProfileSecure({});

  await sendVerificationEmailForUser(userCredential.user, normalizedLanguage);

  return userCredential.user;
};






export const loginUser = async (identifier, password) => {
  const raw = (identifier || "").trim();
  let userCredential;

  // Wenn es eine Email ist → normaler Login
  if (raw.includes("@")) {
    userCredential = await signInWithEmailAndPassword(
      auth,
      raw.toLowerCase(),
      password
    );
  } else {
    const usernameLower = raw.toLowerCase();
    const signInWithUsernameSecure = httpsCallable(
      getFunctions(),
      "signInWithUsernameSecure"
    );
    let result;
    try {
      result = await signInWithUsernameSecure({
        username: usernameLower,
        password,
        apiKey: app.options.apiKey,
      });
    } catch (error) {
      const loginError = new Error("INVALID_LOGIN");
      loginError.code = "auth/invalid-login-credentials";
      throw loginError;
    }
    const customToken = String(result?.data?.customToken || "");
    if (!customToken) {
      throw new Error("USERNAME_LOGIN_FAILED");
    }
    userCredential = await signInWithCustomToken(auth, customToken);
  }

  const userRef = doc(db, "users", userCredential.user.uid);

  try {
    const userSnap = await getDoc(userRef);

 if (userSnap.exists()) {
  const userData = userSnap.data() || {};

  const updates = {};

  if (!userData.timeZone) {
    updates.timeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "Europe/Berlin";
  }

  const storedLanguage =
    localStorage.getItem("vaultquest_language");

  if (
    storedLanguage &&
    ["de", "en"].includes(storedLanguage) &&
    userData.language !== storedLanguage
  ) {
    updates.language = storedLanguage;
  }

  if (Object.keys(updates).length > 0) {
    await updateDoc(userRef, updates);
  }
}
  } catch (error) {
    console.warn("Login succeeded, but profile post-login sync failed:", error?.code || error?.message || "unknown");
  }

  return userCredential.user;
};






// Passwort zurücksetzen
export const resetPassword = async (email, language = "en") => {
  const normalizedLanguage = language === "de" ? "de" : "en";
  const functions = getFunctions();
  const sendPasswordResetEmailCustom = httpsCallable(
    functions,
    "sendPasswordResetEmailCustom"
  );

  return sendPasswordResetEmailCustom({
    email,
    language: normalizedLanguage,
  });
};

export const sendPasswordChangedSecurityEmail = async ({
  email,
  language = "en",
  resetCodeHash,
}) => {
  const normalizedLanguage = language === "de" ? "de" : "en";
  const functions = getFunctions();
  const sendSecurityEmail = httpsCallable(
    functions,
    "sendPasswordChangedSecurityEmail"
  );

  return sendSecurityEmail({
    email,
    language: normalizedLanguage,
    resetCodeHash,
  });
};









export const logoutUser = () => {
  setLogoutInProgress(true);

  return unregisterCurrentNativePushToken()
    .catch((error) => {
      console.warn("Native push unregister skipped during logout:", error?.code || error?.message || "unknown");
    })
    .then(() => signOut(auth))
    .catch((error) => {
      setLogoutInProgress(false);
      throw error;
    });
};
